import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, CheckboxGroup, Modal } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

// Which of a whole series' show types join the collection. Only matching dates
// are mirrored, so every pass in the collection covers just those.
const MembershipShowTypesModal = ({ membership, open, onClose }) => {
  const { handleUpdateMembershipShowTypes } = useSeriesShowContext();
  const available = membership.available_show_types || [];
  // A stored show type the series has since dropped can't be shown or unticked,
  // so it is left out from the start.
  const initialChoice = () => (membership.show_types || available).filter((showType) => available.includes(showType));
  const [chosen, setChosen] = useState(initialChoice);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setChosen(initialChoice());
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const coversAll = available.every((showType) => chosen.includes(showType));
      await handleUpdateMembershipShowTypes(membership, coversAll ? null : chosen);
      toastSuccess(`${membership.name} show types saved. Dates update in a moment.`);
      onClose();
    } catch (e) {
      const errors = e?.response?.data?.errors;
      toastError(Array.isArray(errors) && errors.length > 0 ? errors[0] : "Could not save the show types. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      icon={false}
      width={480}
      title={`Show types from ${membership.name}`}
      description="Only dates of these show types join this collection, so its passes cover just those."
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={saving || chosen.length === 0}
            data-testid="membership-show-types-save"
          >
            Save
          </Button>
        </>
      )}
    >
      <div data-testid={`membership-show-types-${membership.id}`}>
        <CheckboxGroup
          values={chosen}
          onChange={setChosen}
          items={available.map((showType) => ({ value: showType, label: showType }))}
        />
        {chosen.length === 0 && <div className="text-danger small mt-1">Pick at least one show type.</div>}
      </div>
    </Modal>
  );
};

MembershipShowTypesModal.propTypes = {
  membership: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    show_types: PropTypes.arrayOf(PropTypes.string),
    available_show_types: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MembershipShowTypesModal;
